import type { TOmanFormatData, TOmanFormatItem } from '../types/oman.types'

// ─── Generic helpers ──────────────────────────────────────────────────────────

const getNestedValue = (source: Record<string, unknown>, path: string[]): unknown =>
    path.reduce<unknown>((current, key) => {
        if (current && typeof current === 'object' && key in (current as Record<string, unknown>)) {
            return (current as Record<string, unknown>)[key]
        }
        return undefined
    }, source)

const getFirstNumber = (source: Record<string, unknown>, paths: string[][]) => {
    for (const path of paths) {
        const value = getNestedValue(source, path)
        const numericValue = Number(value)
        if (!Number.isNaN(numericValue)) return numericValue
    }
    return 0
}

const pickLocalized = (obj: Record<string, string> | undefined, prefersNative: boolean) =>
    obj ? (prefersNative ? obj.native || obj.english || '' : obj.english || obj.native || '') : ''

// ─── Freight extraction ───────────────────────────────────────────────────────
//
// Hydra returns the current delivery freight at `hydra.marketing.terms.freight.amount`.
// Several UI components need to read this number, so centralize the traversal
// + NaN-guard here instead of open-coding the cast chain in each component.
export const getHydraFreightAmount = (formatData: unknown): number => {
    const hydra = (formatData as Record<string, unknown> | null)?.hydra as Record<string, unknown> | undefined
    const marketing = hydra?.marketing as Record<string, unknown> | undefined
    const terms = marketing?.terms as Record<string, unknown> | undefined
    const freight = terms?.freight as Record<string, unknown> | undefined
    const amount = Number(freight?.amount ?? 0)
    return Number.isNaN(amount) ? 0 : amount
}

// ─── Display name lookup ──────────────────────────────────────────────────────

const buildDisplayNameLookup = (rawFormat: Record<string, unknown>, prefersNative: boolean) => {
    const lookup = new Map<string, string>()
    const sources = [getNestedValue(rawFormat, ['ushop', 'u23', 'formatProducts']), getNestedValue(rawFormat, ['ushop', 'productsDisplay'])]

    for (const source of sources) {
        if (!Array.isArray(source)) continue
        for (const entry of source) {
            const prod = entry as Record<string, unknown>
            const code = String(prod.item_code ?? '')
            if (!code) continue

            const titleInOrderSummary = prod.titleInOrderSummary as Record<string, string> | undefined
            const itemName = prod.item_name as Record<string, string> | string | undefined
            const displayName = titleInOrderSummary
                ? pickLocalized(titleInOrderSummary, prefersNative)
                : typeof itemName === 'object' && itemName
                  ? pickLocalized(itemName, prefersNative)
                  : typeof itemName === 'string'
                    ? itemName
                    : ''

            if (displayName) lookup.set(code, displayName)
        }
        if (lookup.size > 0) break
    }
    return lookup
}

// ─── Raw item resolution ──────────────────────────────────────────────────────

const resolveRawItems = (rawFormat: Record<string, unknown>): unknown[] => {
    const candidates: unknown[] = [
        rawFormat.items,
        getNestedValue(rawFormat, ['hydra', 'marketing', 'items']),
        getNestedValue(rawFormat, ['ushop', 'items']),
        getNestedValue(rawFormat, ['ushop', 'detail_page', 'items'])
    ]
    return (candidates.find(Array.isArray) as unknown[] | undefined) ?? []
}

const mapRawItem = (item: unknown, displayLookup: Map<string, string>, prefersNative: boolean): TOmanFormatItem => {
    const r = item as Record<string, unknown>
    const nested = r.item as Record<string, unknown> | undefined
    const terms = r.terms as Record<string, unknown> | undefined
    const catalogContent = (r.catalogSlide as Record<string, unknown> | undefined)?.content as Record<string, unknown> | undefined

    const itemCode = String(r.item_code ?? (nested?.id as Record<string, unknown> | undefined)?.unicity ?? '')

    const itemNameField = r.item_name as Record<string, string> | string | undefined
    const rawName =
        typeof itemNameField === 'object' && itemNameField
            ? pickLocalized(itemNameField, prefersNative)
            : typeof itemNameField === 'string'
              ? itemNameField
              : String(r.name ?? catalogContent?.description ?? '')

    return {
        item_code: itemCode,
        name: displayLookup.get(itemCode) ?? rawName,
        qty: Number(r.qty ?? r.quantity ?? 0),
        price: Number(r.price ?? terms?.priceEach ?? terms?.price ?? 0),
        pv: r.pv != null ? Number(r.pv) : terms?.pvEach != null ? Number(terms.pvEach) : terms?.pv != null ? Number(terms.pv) : undefined,
        type: r.type as 'subscription' | 'onetime' | undefined
    }
}

// ─── Main mapper ──────────────────────────────────────────────────────────────

export const mapOmanFormatData = (rawFormat: Record<string, unknown>, uuidOverride?: string): TOmanFormatData => {
    // `formatShipping` only exposes display-ready strings: `name` (localised
    // object), `email`, `phone`, and a comma-joined `address` line. It is the
    // right source for everything we render on the payment page directly.
    const formatShipping =
        (getNestedValue(rawFormat, ['ushop', 'formatShipping']) as Record<string, unknown> | undefined) ??
        (getNestedValue(rawFormat, ['ushop', 'u23', 'formatShipping']) as Record<string, unknown> | undefined) ??
        {}
    // `detail_page` is the source of truth for individual address fields
    // (firstname/lastname, address1/address2/city/state/country/zip). The
    // legacy U-Shop billingHelper uses these for the credit-card payload and
    // billing summary; without them, fields show up as `undefined` even though
    // the data is present in the raw response.
    const detailPage =
        (getNestedValue(rawFormat, ['ushop', 'detail_page']) as Record<string, unknown> | undefined) ??
        (getNestedValue(rawFormat, ['ushop', 'u23', 'detail_page']) as Record<string, unknown> | undefined) ??
        {}
    // `formatDetail.ship_to.address` is the display-ready address string the
    // legacy app surfaces on the payment page. Use it as a fallback when
    // `formatShipping.address` is empty so guests see their address right
    // after enrollment.
    const shipToAddress =
        (getNestedValue(rawFormat, ['ushop', 'u23', 'formatDetail', 'ship_to', 'address']) as string | undefined) ??
        (getNestedValue(rawFormat, ['ushop', 'formatDetail', 'ship_to', 'address']) as string | undefined)
    const formatShippingName = (getNestedValue(formatShipping, ['name']) as Record<string, unknown> | undefined) ?? {}
    const prefersNative =
        typeof window !== 'undefined' && (document.documentElement.lang?.toLowerCase().startsWith('ar') || navigator.language?.toLowerCase().startsWith('ar'))
    const fullname = prefersNative
        ? ((formatShippingName.native as string | undefined) ?? (formatShippingName.english as string | undefined))
        : ((formatShippingName.english as string | undefined) ?? (formatShippingName.native as string | undefined))

    const rawItems = resolveRawItems(rawFormat)
    const displayLookup = buildDisplayNameLookup(rawFormat, prefersNative)

    const pickString = (...candidates: unknown[]): string | undefined => {
        for (const value of candidates) {
            if (typeof value === 'string' && value.length > 0) return value
        }
        return undefined
    }

    return {
        uuid: uuidOverride ?? String(rawFormat.uuid ?? ''),
        fullname: pickString(fullname, detailPage.fullname, rawFormat.fullname),
        firstname: pickString(detailPage.firstname_en, detailPage.firstname, rawFormat.firstname),
        lastname: pickString(detailPage.lastname_en, detailPage.lastname, rawFormat.lastname),
        email: pickString(formatShipping.email, detailPage.email, rawFormat.email),
        phone: pickString(formatShipping.phone, detailPage.phone, rawFormat.phone),
        // Keep address1 separate so the payment page can render:
        // address1, address2 / city, state, country.
        address: pickString(detailPage.address, detailPage.address1, rawFormat.address, rawFormat.address1, formatShipping.address, shipToAddress),
        address2: pickString(detailPage.address2, rawFormat.address2),
        city: pickString(detailPage.city, rawFormat.city),
        state: pickString(detailPage.state, rawFormat.state),
        country: pickString(detailPage.country, rawFormat.country),
        shipment_options: (rawFormat.shipment_options as string) ?? (getNestedValue(rawFormat, ['ushop', 'detail_page', 'shipment_options']) as string),
        freight: getFirstNumber(rawFormat, [
            ['hydra', 'marketing', 'terms', 'freight', 'amount'],
            ['hydra', 'marketing', 'terms', 'freight', 'amout'],
            ['freight']
        ]),
        subtotal: getFirstNumber(rawFormat, [['hydra', 'marketing', 'terms', 'subtotal', 'amount'], ['subtotal']]),
        total: getFirstNumber(rawFormat, [['hydra', 'marketing', 'terms', 'total', 'amount'], ['total']]),
        product_credit: getFirstNumber(rawFormat, [['hydra', 'marketing', 'terms', 'product_credit', 'amount'], ['product_credit']]),
        tax: {
            amount: getFirstNumber(rawFormat, [['hydra', 'marketing', 'terms', 'tax', 'amount'], ['tax']]),
            percentage: getFirstNumber(rawFormat, [['hydra', 'marketing', 'terms', 'tax', 'percentage']])
        },
        items: rawItems.map(item => mapRawItem(item, displayLookup, prefersNative)),
        hasSubscription: Boolean(rawFormat.hasSubscription)
    }
}
